import type { Transaction } from '@/types';
import Icon from './Icon';

interface TransactionDetailPanelProps {
  transaction: Transaction | null;
  onClose: () => void;
}

// Purpose code descriptions for common SEPA codes
const purposeCodeDescriptions: Record<string, string> = {
  SALA: 'Salary Payment',
  PENS: 'Pension Payment',
  SSBE: 'Social Security Benefit',
  RENT: 'Rent Payment',
  LOAN: 'Loan Payment',
  TAXS: 'Tax Payment',
  SUPP: 'Supplier Payment',
  DIVI: 'Dividend Payment',
  BONU: 'Bonus Payment',
  COMM: 'Commission',
  TRAD: 'Trade Services',
  CHAR: 'Charity Payment',
  GOVT: 'Government Payment',
  INSU: 'Insurance Premium',
  INTC: 'Intra-Company Payment',
  PAYR: 'Payroll',
  STDY: 'Study',
  CASH: 'Cash Management',
  OTHR: 'Other',
};

export const TransactionDetailPanel = ({ transaction, onClose }: TransactionDetailPanelProps) => {
  if (!transaction) return null;

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatIban = (iban: string) => {
    // Format IBAN in groups of 4 for readability
    return iban.replace(/(.{4})/g, '$1 ').trim();
  };

  const getPurposeDescription = (code: string | undefined) => {
    if (!code) return null;
    return purposeCodeDescriptions[code.toUpperCase()] || code;
  };

  const hasEnrichmentData =
    transaction.creditorIban ||
    transaction.debtorIban ||
    transaction.endToEndId ||
    transaction.purposeCode ||
    transaction.bankTransactionCode ||
    transaction.remittanceStructured;

  return (
    <>
      {/* Invisible backdrop for click-outside-to-close */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        data-testid="panel-backdrop"
      />

      {/* Panel */}
      <div
        className="fixed right-0 top-0 h-full w-full max-w-md bg-white dark:bg-slate-900 shadow-2xl z-50 transform transition-transform duration-300 ease-out overflow-y-auto overscroll-contain"
        data-testid="transaction-detail-panel"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 p-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Transaction Details</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Close panel"
            data-testid="close-panel-button"
          >
            <Icon name="close" className="text-slate-500 dark:text-slate-400" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Amount Section */}
          <div className="text-center py-4">
            <div
              className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-3 ${
                transaction.amount < 0
                  ? 'bg-red-100 dark:bg-red-900/30'
                  : 'bg-green-100 dark:bg-green-900/30'
              }`}
            >
              <Icon
                name={transaction.amount < 0 ? 'arrow_upward' : 'arrow_downward'}
                size={30}
                className={
                  transaction.amount < 0
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-green-600 dark:text-green-400'
                }
              />
            </div>
            <p
              className={`text-3xl font-bold ${
                transaction.amount < 0
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-green-600 dark:text-green-400'
              }`}
            >
              {transaction.amount > 0 ? '+' : ''}
              {formatCurrency(transaction.amount, transaction.currency)}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Balance after: {formatCurrency(transaction.runningBalance, transaction.currency)}
            </p>
          </div>

          {/* Standard Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Transaction Information
            </h3>

            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
              <DetailRow label="Description" value={transaction.description} />
              <DetailRow label="Transaction Date" value={formatDate(transaction.transactionDate)} />
              <DetailRow label="Booking Date" value={formatDate(transaction.bookingDate)} />
              <DetailRow label="Type" value={transaction.type} capitalize />
              <DetailRow label="Status" value={transaction.status} capitalize />
              {transaction.category && <DetailRow label="Category" value={transaction.category} />}
              {transaction.notes && <DetailRow label="Notes" value={transaction.notes} />}
            </div>
          </div>

          {/* Enrichment Data */}
          {hasEnrichmentData && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Additional Details
              </h3>

              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
                {transaction.creditorIban && (
                  <DetailRow
                    label="Creditor IBAN"
                    value={formatIban(transaction.creditorIban)}
                    mono
                  />
                )}
                {transaction.debtorIban && (
                  <DetailRow
                    label="Debtor IBAN"
                    value={formatIban(transaction.debtorIban)}
                    mono
                  />
                )}
                {transaction.endToEndId && (
                  <DetailRow
                    label="End-to-End ID"
                    value={transaction.endToEndId}
                    mono
                    copyable
                  />
                )}
                {transaction.purposeCode && (
                  <DetailRow
                    label="Purpose"
                    value={`${transaction.purposeCode} - ${getPurposeDescription(transaction.purposeCode)}`}
                  />
                )}
                {transaction.bankTransactionCode && (
                  <DetailRow
                    label="Bank Transaction Code"
                    value={transaction.bankTransactionCode}
                    mono
                  />
                )}
                {transaction.remittanceStructured && (
                  <DetailRow
                    label="Remittance Info"
                    value={transaction.remittanceStructured}
                    multiline
                  />
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
};

interface DetailRowProps {
  label: string;
  value: string;
  mono?: boolean;
  capitalize?: boolean;
  copyable?: boolean;
  multiline?: boolean;
}

const DetailRow = ({ label, value, mono, capitalize, copyable, multiline }: DetailRowProps) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
  };

  return (
    <div className={multiline ? 'space-y-1' : 'flex justify-between items-start gap-4'}>
      <span className="text-sm text-slate-500 dark:text-slate-400 shrink-0">{label}</span>
      <div className="flex items-center gap-2">
        <span
          className={`text-sm text-slate-900 dark:text-white ${multiline ? '' : 'text-right'} ${
            mono ? 'font-mono text-xs' : ''
          } ${capitalize ? 'capitalize' : ''} ${multiline ? 'break-all' : ''}`}
        >
          {value}
        </span>
        {copyable && (
          <button
            onClick={handleCopy}
            className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            title="Copy to clipboard"
          >
            <Icon name="content_copy" size={16} className="text-slate-400" />
          </button>
        )}
      </div>
    </div>
  );
};
